import { FormattedMessage, useIntl } from 'react-intl';
import { useSettingsQuery } from '../../lib/core/SettingsQuery';
import { Text, Box, Heading, Container } from '../../components';
import { Wizard, useWizardContext } from '../../components/Wizard';
import { pushRoute } from '../../components/Link';
import messages from '../RedeemCoupon/messages';
import CheckCouponForm from './CheckCouponForm';
import PhoneStep from './Step/PhoneStep';
import SummaryStep from './Step/SummaryStep';
import {
  CheckoutValueContextProvider,
  useValue
} from '../CheckoutValueContext';
import {
  redeemCouponPageQuery,
  productQuery,
  productQueryVariables,
  SanityImageOptionFit,
  SanityImageOptionCrop,
  settings_settings,
  CurrentUser
} from '../../core-types';
import { gql, useQuery } from '@apollo/client';
import { PRODUCT_FRAGMENT } from '../../lib/core/fragments';
import { captureException } from '../../lib/sentry';
import { useCurrentUser } from '../../lib/core/useCurrentUser';
import CurrentUserSubscription from '../BuyProduct/CurrentUserSubscription';

type Props = {
  data: redeemCouponPageQuery;
  step?: string;
};

const PRODUCT_BY_ID = gql`
  ${PRODUCT_FRAGMENT}
  query productQuery($id: ID!, $productImageOptions: SanityImageOptions) {
    product(id: $id) {
      ...Product
      partners {
        id
      }
    }
  }
`;

const RedeemCouponWizard = ({
  productId,
  step,
  settings,
  currentUser
}: {
  productId: string;
  step: string;
  settings: settings_settings;
  currentUser: CurrentUser | null;
}) => {
  const intl = useIntl();
  const stepIndex = step ? parseInt(step) : 0;
  const productQueryProps = useQuery<productQuery, productQueryVariables>(
    PRODUCT_BY_ID,
    {
      variables: {
        id: productId,
        productImageOptions: {
          width: 750,
          height: 750,
          fit: SanityImageOptionFit.crop,
          crop: SanityImageOptionCrop.entropy
        }
      }
    }
  );

  if (
    !productQueryProps ||
    !productQueryProps.data ||
    !productQueryProps.data.product
  ) {
    return null;
  }
  const product = productQueryProps.data.product;

  if (!productQueryProps.loading && !product) {
    const err = new Error('Product not found');
    err.code = 'ENOENT';
    throw err;
  }

  const steps = [
    function ValidCouponStep() {
      const { goNext } = useWizardContext();
      return (
        <PhoneStep
          product={product}
          goNext={goNext}
          privacyPageSlug={
            settings.privacyPageSlug && settings.privacyPageSlug.current
          }
          currentUser={currentUser}
        />
      );
    },
    // Since we added the user consent update before checkout, we don't need to add it to the steps array
    // function AcceptTermsStep() {
    //   const { goNext } = useWizardContext();
    //   return <TermsStep goNext={goNext} />;
    // },
    function SummaryStepFunc() {
      return (
        <SummaryStep product={product} currentUser={currentUser} intl={intl} />
      );
    }
  ];

  const StepComponent = steps[stepIndex];

  return (
    <Wizard
      stepIndex={stepIndex}
      stepCount={steps.length}
      onStepChange={props => {
        if (props.nextStepIndex !== null) {
          const nextStep = steps[props.nextStepIndex];
          if (!nextStep) {
            captureException(new Error('Next step not found'), {});
            return;
          }
          if (stepIndex === props.nextStepIndex) {
            return;
          }
          pushRoute({
            route: 'redeem-coupon',
            params: {
              step: props.nextStepIndex
            },
            options: {
              shallow: true
            },
            intl
          });
        }
      }}
    >
      <StepComponent />
    </Wizard>
  );
};

const RedeemCoupon = ({ step, data }: Props) => {
  const intl = useIntl();
  const { data: settingsData } = useSettingsQuery();
  const { currentUser } = useCurrentUser();
  const [state, dispatch] = useValue();
  const { redeemCouponPage } = data;
  const { productId } = state;

  if (!settingsData || !settingsData.settings) {
    return null;
  }

  const currentSubscription = currentUser?.currentSubscription;

  return (
    <>
      <CurrentUserSubscription />
      {!currentSubscription && (
        <Box>
          {step && productId && (
            <RedeemCouponWizard
              step={step}
              productId={productId}
              settings={settingsData.settings}
              currentUser={currentUser}
            />
          )}
          {(!step || !productId) && (
            <Container variant="narrow">
              {redeemCouponPage && (
                <Heading textAlign="center">
                  {redeemCouponPage.heading || redeemCouponPage.title}
                </Heading>
              )}
              <Text as="p" textAlign="center" mb={6}>
                <FormattedMessage {...messages.description} />
              </Text>
              <CheckCouponForm
                onValidCouponEntered={({ validCoupon, productId }) => {
                  dispatch({
                    type: 'VALID_COUPON_ENTERED',
                    validCoupon,
                    productId
                  });
                  pushRoute({
                    route: 'redeem-coupon',
                    params: {
                      step: 0
                    },
                    intl
                  });
                }}
              />
            </Container>
          )}
        </Box>
      )}
    </>
  );
};

function RedeemCouponWithContext(props: Props) {
  return (
    <CheckoutValueContextProvider storageKey={null}>
      <RedeemCoupon {...props} />
    </CheckoutValueContextProvider>
  );
}

export default RedeemCouponWithContext;
